import {
  MOCK_PEOPLE,
  SEED_JOB_LIMITS,
  SEED_ROLE_CATALOG,
  FINANCE_DIRECTOR_POSITION_ID,
  createRng,
  type MockPerson,
  type VenueSlot,
} from '@lg/shared';
import { COPIED_ENTITY_SETS } from '../verify.js';
import type {
  ApprovalLimit,
  D365User,
  LegalEntity,
  Position,
  RoleAssignment,
  Vendor,
  Worker,
} from '../types.js';
import type { MockState } from './state.js';

/**
 * Synthetic D365 seed (spec 12.3). Every name, number and date is generated
 * from `seed`; dates are relative to `asOf` so a reused demo stays current.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const TEMPLATE_COMPANY_ID = 'LTPL';

/**
 * Template companies: the source each venue is copied from, with its copy project.
 * LTPL is the original; LTPC (convention centers) is derived from it without using
 * the RNG, so adding it changes none of the seeded demo numbers.
 */
export const TEMPLATE_COMPANIES = [
  { id: 'LTPL', name: 'Legends Template Company', searchName: 'LG TEMPLATE', copyProject: 'LG-VENUE-COPY' },
  {
    id: 'LTPC',
    name: 'Legends Convention Template',
    searchName: 'LG CONVENTION TPL',
    copyProject: 'LG-CC-COPY',
  },
] as const;

export const isTemplateCompany = (id: string): boolean => TEMPLATE_COMPANIES.some((t) => t.id === id);

/** Source company of a copy project; unknown projects copy from LTPL. */
export const copySourceFor = (projectName: string): string =>
  TEMPLATE_COMPANIES.find((t) => t.copyProject === projectName)?.id ?? TEMPLATE_COMPANY_ID;

/**
 * Adds any template company missing from the state (LTPC), copied from LTPL: same job
 * limits and vendors, a smaller setup (fewer accounts and categories, few project groups).
 * Also upgrades states saved by earlier builds.
 */
export function ensureTemplateCompanies(s: MockState): void {
  const base = s.entityCounts[TEMPLATE_COMPANY_ID] ?? {};
  for (const t of TEMPLATE_COMPANIES) {
    if (s.companies.some((c) => c.id === t.id)) continue;
    s.companies.splice(1, 0, {
      id: t.id,
      name: t.name,
      searchName: t.searchName,
      timeZone: 'America/Chicago',
      address: null,
      region: null,
    });
    for (const l of s.approvalLimits.filter((x) => x.companyId === TEMPLATE_COMPANY_ID))
      s.approvalLimits.push({ ...l, companyId: t.id });
    for (const v of s.vendors.filter((x) => x.companyId === TEMPLATE_COMPANY_ID))
      s.vendors.push({ ...v, companyId: t.id });
    const counts = { ...base };
    const less = (key: string, n: number) => {
      if (counts[key] !== undefined) counts[key] = Math.max(0, (counts[key] ?? 0) - n);
    };
    less('MainAccounts', 42);
    less('ProcurementCategories', 15);
    if (counts['ProjectGroups'] !== undefined) counts['ProjectGroups'] = 3;
    less('ProjectCategories', 12);
    s.entityCounts[t.id] = counts;
  }
}
/**
 * Group IDs a copied company has, in order. A company has as many vendor and project groups
 * as its VendorGroups / ProjectGroups record counts (so LTPC gets the first 3 project groups),
 * and every customer group once anything was copied. Fixed lists: no RNG, so the seeded
 * demo numbers do not move.
 */
export const MOCK_CUSTOMER_GROUPS = ['EVENTS', 'SPONSOR', 'PROMOTER', 'TENANT', 'INTERCO'] as const;
export const MOCK_VENDOR_GROUPS = [
  'SERVICES',
  'STAFFING',
  'FB',
  'UTILITIES',
  'PROMOTER',
  'EQUIPMENT',
  'MARKETING',
  'SECURITY',
  'TICKETING',
  'PARKING',
  'MAINT',
  'PROF',
  'INTERCO',
  'OTHER',
] as const;
export const MOCK_PROJECT_GROUPS = [
  'INTERNAL',
  'EVENT-TM',
  'EVENT-FP',
  'SPONSOR',
  'PARKING',
  'FB',
  'TICKETING',
  'CAPEX',
  'CC-EVENT',
  'OVERHEAD',
] as const;

/** Customers in each template company; the copy brings them into the venue. Fictional. */
const TEMPLATE_CUSTOMERS = [
  { customerAccount: 'LC-0001', name: 'Harborline Events Group', group: 'EVENTS' },
  { customerAccount: 'LC-0002', name: 'Crescent Sponsorship Partners', group: 'SPONSOR' },
  { customerAccount: 'LC-0003', name: 'Brightwater Promotions', group: 'PROMOTER' },
] as const;

/**
 * Adds the master data collections (customers, project contracts, projects) to states saved
 * by builds before they existed, and the template customers to every template company.
 */
export function ensureMasterData(s: MockState): void {
  const loose = s as Partial<MockState>;
  loose.customers ??= [];
  loose.projectContracts ??= [];
  loose.projects ??= [];
  loose.workCalendars ??= [];
  loose.jobs ??= [];
  for (const t of TEMPLATE_COMPANIES) {
    for (const c of TEMPLATE_CUSTOMERS) {
      if (s.customers.some((x) => x.companyId === t.id && x.customerAccount === c.customerAccount)) continue;
      s.customers.push({ ...c, currency: 'USD', companyId: t.id });
    }
  }
}

/**
 * The groups of Legends' template company 1100 (TRN, queried 2026-10-27), so a request built for
 * the TRN passes the group checks when the mock stands in for it. Every copied company has them
 * on top of the demo groups; the demo's seeded numbers don't use them.
 */
export const LEGENDS_1100_GROUPS = {
  customer: [
    'CG100',
    'CG110',
    'CG120',
    'CG130',
    'CG140',
    'CG150',
    'CG700',
    'CG800',
    'CG900',
    'CG950',
    'CG999',
  ],
  vendor: [
    'VG110',
    'VG120',
    'VG130',
    'VG140',
    'VG150',
    'VG160',
    'VG170',
    'VG180',
    'VG190',
    'VG200',
    'VG210',
    'VG220',
    'VG230',
    'VG240',
    'VG250',
    'VG260',
    'VG270',
    'VG280',
    'VG900',
    'VG999',
  ],
  project: [
    'VEN_MGMT',
    'MGMT_FF',
    'MGMT_KPI',
    'MGMT_VF',
    'BILL_ADJ',
    'OPEX_INT',
    'PROJ_INT',
    'CAPEX_INT',
    'CONSULT',
    'MEDIA',
  ],
} as const;

export function masterDataGroupsFor(
  s: MockState,
  companyId: string,
): { customerGroups: string[]; vendorGroups: string[]; projectGroups: string[] } {
  const counts = s.entityCounts[companyId] ?? {};
  const copied = Object.values(counts).some((n) => n > 0);
  const legends = (groups: readonly string[]): string[] => (copied ? [...groups] : []);
  return {
    customerGroups: copied ? [...MOCK_CUSTOMER_GROUPS, ...LEGENDS_1100_GROUPS.customer] : [],
    vendorGroups: [
      ...MOCK_VENDOR_GROUPS.slice(0, counts['VendorGroups'] ?? 0),
      ...legends(LEGENDS_1100_GROUPS.vendor),
    ],
    projectGroups: [
      ...MOCK_PROJECT_GROUPS.slice(0, counts['ProjectGroups'] ?? 0),
      ...legends(LEGENDS_1100_GROUPS.project),
    ],
  };
}

export const SEED_VENUES = [
  {
    id: 'V001',
    name: 'Legends - Harborview Stadium',
    region: 'Northeast',
    tz: 'America/New_York',
    address: { street: '100 Pier Road', city: 'Providence', state: 'RI', zip: '02903' },
  },
  {
    id: 'V002',
    name: 'Legends - Summit Arena',
    region: 'Mountain',
    tz: 'America/Denver',
    address: { street: '2200 Ridgeline Ave', city: 'Denver', state: 'CO', zip: '80202' },
  },
  {
    id: 'V003',
    name: 'Legends - Lakeshore Convention Center',
    region: 'Midwest',
    tz: 'America/Chicago',
    address: { street: '45 Shoreline Blvd', city: 'Milwaukee', state: 'WI', zip: '53202' },
  },
] as const;

const SLOT_DEFS: Record<
  VenueSlot,
  { suffix: string; title: string; jobId: string; reportsTo: VenueSlot | null }
> = {
  GM: { suffix: 'GM', title: 'General manager', jobId: 'VENUE-GM', reportsTo: null },
  FM: { suffix: 'FM', title: 'Finance manager', jobId: 'FIN-MGR', reportsTo: 'GM' },
  BUY: { suffix: 'BUY', title: 'Buyer', jobId: 'BUYER', reportsTo: 'FM' },
  AP: { suffix: 'AP', title: 'AP clerk', jobId: 'AP-CLERK', reportsTo: 'FM' },
  PM: { suffix: 'PM', title: 'Project manager', jobId: 'PROJ-MGR', reportsTo: 'GM' },
};

const SLOT_ROLES: Record<VenueSlot, string[]> = {
  GM: ['LEDGERACCOUNTINGMANAGER'],
  FM: ['VENDINVOICEACCOUNTSPAYABLEMANAGER', 'LEDGERACCOUNTINGMANAGER'],
  BUY: ['VENDPURCHASINGAGENT'],
  AP: ['VENDINVOICEACCOUNTSPAYABLECLERK'],
  PM: ['PROJPROJECTMANAGER'],
};

export const REGIONAL_POSITION_ID = 'REG-FIN-01';
export const CORPORATE_POSITION_ID = 'CORP-CFO';

/**
 * The finance director the GL accountant reports to (FINANCE_DIRECTOR_POSITION_ID), held by the
 * regional finance director in the mock.
 */
const FINANCE_DIRECTOR_POSITION: Omit<Position, 'holderName' | 'holderUpn'> = {
  positionId: FINANCE_DIRECTOR_POSITION_ID,
  title: 'Finance director',
  jobId: 'FINANCE-DIRECTOR',
  companyId: null,
  departmentId: 'REG-FIN',
  reportsToPositionId: CORPORATE_POSITION_ID,
  holderPersonnelNumber: '900001',
};

/**
 * Upgrades states saved by earlier builds (the browser demo keeps its mock D365 in localStorage):
 * adds the finance director position and the approval limits of jobs added to the seed since,
 * for every company that has the seed limits. Nothing saved is changed or removed.
 */
export function ensureSeedPeople(s: MockState): void {
  if (!s.positions.some((p) => p.positionId === FINANCE_DIRECTOR_POSITION_ID)) {
    const regional = s.positions.find((p) => p.positionId === REGIONAL_POSITION_ID);
    s.positions.push({
      ...FINANCE_DIRECTOR_POSITION,
      holderName: regional?.holderName ?? null,
      holderUpn: regional?.holderUpn ?? null,
    });
  }
  const seedJobs = new Set(SEED_JOB_LIMITS.map((l) => l.jobId));
  const companies = new Set(s.approvalLimits.filter((l) => seedJobs.has(l.jobId)).map((l) => l.companyId));
  for (const companyId of companies)
    for (const l of SEED_JOB_LIMITS)
      if (
        !s.approvalLimits.some(
          (x) => x.companyId === companyId && x.jobId === l.jobId && x.documentType === l.documentType,
        )
      )
        s.approvalLimits.push({ companyId, ...l });
}

const VENDOR_PREFIXES = [
  'Apex',
  'Bayline',
  'Cobalt',
  'Driftwood',
  'Evergreen',
  'Frontier',
  'Granite',
  'Ironbridge',
  'Juniper',
  'Keystone',
];
const VENDOR_SUFFIXES = [
  'Concessions Supply',
  'Facility Services',
  'Event Staffing',
  'Linen Co.',
  'Sound & Light',
  'Beverage Distributors',
  'Security Group',
  'Turf Management',
  'Print Works',
  'Waste Solutions',
];

const ENTITY_COUNT_RANGES: Record<(typeof COPIED_ENTITY_SETS)[number], [number, number]> = {
  MainAccounts: [380, 460],
  FinancialDimensionValues: [120, 180],
  NumberSequenceReferences: [60, 90],
  VendorGroups: [8, 14],
  PaymentTerms: [6, 12],
  MethodsOfPayment: [4, 8],
  ProcurementCategories: [40, 70],
  SalesTaxGroups: [10, 25],
  VendorPostingProfiles: [3, 6],
  ProjectGroups: [5, 10],
  ProjectCategories: [20, 40],
  VendorsV2: [30, 30],
};

export const userIdFor = (p: Pick<MockPerson, 'firstName' | 'lastName'>): string =>
  `${p.firstName.charAt(0)}${p.lastName}`.toLowerCase().slice(0, 20);

const isoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function buildMockState(seed: number, asOf: Date): MockState {
  const rng = createRng(seed);
  const asOfMs = asOf.getTime();

  const companies: LegalEntity[] = [
    {
      id: TEMPLATE_COMPANY_ID,
      name: 'Legends Template Company',
      searchName: 'LG TEMPLATE',
      timeZone: 'America/New_York',
      address: null,
      region: null,
    },
    ...SEED_VENUES.map((v) => ({
      id: v.id,
      name: v.name,
      searchName: v.name.replace('Legends - ', '').toUpperCase().slice(0, 20),
      timeZone: v.tz,
      address: { ...v.address },
      region: v.region,
    })),
  ];

  const positions: Array<Omit<Position, 'holderName' | 'holderUpn'>> = [
    {
      positionId: CORPORATE_POSITION_ID,
      title: 'Chief financial officer',
      jobId: 'CFO',
      companyId: null,
      departmentId: 'CORP-FIN',
      reportsToPositionId: null,
      holderPersonnelNumber: '900002',
    },
    {
      positionId: REGIONAL_POSITION_ID,
      title: 'Regional finance director',
      jobId: 'REG-FIN-DIR',
      companyId: null,
      departmentId: 'REG-FIN',
      reportsToPositionId: CORPORATE_POSITION_ID,
      holderPersonnelNumber: '900001',
    },
    { ...FINANCE_DIRECTOR_POSITION },
  ];
  const workers: Worker[] = [];
  const users: D365User[] = [];
  const roleAssignments: RoleAssignment[] = [];
  const approvalLimits: ApprovalLimit[] = [];

  const employmentStart = (): string => isoDate(asOfMs - rng.int(200, 2400) * DAY_MS);

  for (const p of MOCK_PEOPLE) {
    if (!p.placement) continue;
    const userId = userIdFor(p);
    users.push({ userId, upn: p.upn, name: `${p.firstName} ${p.lastName}`, enabled: p.enabled });

    const [scope, slotOrPosition] = p.placement.split(':') as [string, string];
    if (scope === 'corp') {
      const personnelNumber = slotOrPosition === REGIONAL_POSITION_ID ? '900001' : '900002';
      workers.push({
        personnelNumber,
        firstName: p.firstName,
        lastName: p.lastName,
        upn: p.upn,
        companyId: null,
        employmentStart: employmentStart(),
      });
      if (slotOrPosition === REGIONAL_POSITION_ID) {
        for (const v of SEED_VENUES)
          roleAssignments.push({ userId, roleId: 'LEDGERFINANCIALCONTROLLER', companyId: v.id });
      }
      continue;
    }

    const companyId = scope;
    const slot = slotOrPosition as VenueSlot;
    const def = SLOT_DEFS[slot];
    const venueNumber = companyId.slice(1);
    const personnelNumber = `${venueNumber}${String(Object.keys(SLOT_DEFS).indexOf(slot) + 1).padStart(3, '0')}`;
    positions.push({
      positionId: `${companyId}-${def.suffix}`,
      title: def.title,
      jobId: def.jobId,
      companyId,
      departmentId: `${companyId}-OPS`,
      reportsToPositionId: def.reportsTo
        ? `${companyId}-${SLOT_DEFS[def.reportsTo].suffix}`
        : REGIONAL_POSITION_ID,
      holderPersonnelNumber: personnelNumber,
    });
    workers.push({
      personnelNumber,
      firstName: p.firstName,
      lastName: p.lastName,
      upn: p.upn,
      companyId,
      employmentStart: employmentStart(),
    });
    for (const roleId of SLOT_ROLES[slot]) roleAssignments.push({ userId, roleId, companyId });
  }

  // The template company carries the default job limits; venues were copied from it.
  for (const companyId of [TEMPLATE_COMPANY_ID, ...SEED_VENUES.map((v) => v.id)]) {
    for (const l of SEED_JOB_LIMITS) approvalLimits.push({ companyId, ...l });
    for (const documentType of [
      'PurchaseRequisition',
      'PurchaseOrder',
      'VendorInvoice',
      'Journal',
    ] as const) {
      approvalLimits.push({ companyId, jobId: 'CFO', documentType, amountUsd: 2_000_000 });
    }
  }

  const vendorNames = new Set<string>();
  while (vendorNames.size < 30) vendorNames.add(`${rng.pick(VENDOR_PREFIXES)} ${rng.pick(VENDOR_SUFFIXES)}`);
  const vendors: Vendor[] = [...vendorNames].map((name, i) => ({
    vendorId: `LV-${String(i + 1).padStart(4, '0')}`,
    name,
    companyId: TEMPLATE_COMPANY_ID,
  }));

  const templateCounts: Record<string, number> = {};
  for (const set of COPIED_ENTITY_SETS) {
    const [min, max] = ENTITY_COUNT_RANGES[set];
    templateCounts[set] = rng.int(min, max);
  }
  const entityCounts: Record<string, Record<string, number>> = {
    [TEMPLATE_COMPANY_ID]: { ...templateCounts },
  };
  for (const v of SEED_VENUES) entityCounts[v.id] = { ...templateCounts };

  const workflows: MockState['workflows'] = {};
  for (const v of SEED_VENUES) {
    workflows[v.id] = {
      PO: { workflowId: `${v.id}-PO-1`, active: true },
      VI: { workflowId: `${v.id}-VI-1`, active: true },
      JNL: { workflowId: `${v.id}-JNL-1`, active: true },
    };
  }

  const state: MockState = {
    version: 1,
    seed,
    asOf: asOf.toISOString(),
    companies,
    users,
    roles: SEED_ROLE_CATALOG.map((r) => ({ roleId: r.roleId, name: r.name })),
    roleAssignments,
    vendors,
    customers: [],
    projectContracts: [],
    projects: [],
    workCalendars: [],
    jobs: [],
    positions: positions.map((p) => {
      const holder = workers.find((w) => w.personnelNumber === p.holderPersonnelNumber);
      return {
        ...p,
        holderName: holder ? `${holder.firstName} ${holder.lastName}` : null,
        holderUpn: holder?.upn ?? null,
      };
    }),
    workers,
    approvalLimits,
    policies: {
      SigningLimit: SEED_VENUES.map((v) => v.id),
      Purchasing: SEED_VENUES.map((v) => v.id),
    },
    requireExplicitlyAssigned: Object.fromEntries(SEED_VENUES.map((v) => [v.id, true])),
    entityCounts,
    overrides: {},
    taxRegistrationLast4: {},
    workflows,
    executions: {},
    blobs: {},
    failuresConsumed: [],
    counter: 0,
  };
  ensureTemplateCompanies(state);
  ensureMasterData(state);
  return state;
}
